import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Eye,
  Users,
  DollarSign,
  TrendingUp,
  ArrowLeft,
  Loader2,
  Globe,
  Share2,
  Target,
  BarChart3,
  PieChart,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  MousePointer,
  Percent,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

const CampaignAnalyticsPage = () => {
  const { t } = useLanguage();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [campaignId, period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [analyticsRes, campaignRes] = await Promise.all([
        apiClient.get(`/analytics/campaign/${campaignId}?days=${period}`),
        apiClient.get(`/campaigns/${campaignId}`)
      ]);
      setAnalytics(analyticsRes.data);
      setCampaign(campaignRes.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getSourceIcon = (source) => {
    switch (source?.toLowerCase()) {
      case 'google': return '🔍';
      case 'facebook': return '📘';
      case 'twitter': return '🐦';
      case 'instagram': return '📸';
      case 'email': return '📧';
      case 'direct': return '🔗';
      default: return '🌐';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
          </div>
        </main>
      </div>
    );
  }

  const summary = analytics?.summary || {};
  const progressPercent = summary.goal > 0 ? Math.min((summary.total_raised / summary.goal) * 100, 100) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Campaign Analytics</h1>
                <p className="text-muted-foreground">{campaign?.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[140px]">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
              <Link to={`/campaign/${campaignId}`}>
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Campaign
                </Button>
              </Link>
            </div>
          </div>

          {/* Campaign Progress Card */}
          <Card className="mb-8 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  {campaign?.image && (
                    <img 
                      src={campaign.image} 
                      alt={campaign.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  )}
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">{campaign?.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={campaign?.status === 'active' ? 'default' : 'secondary'}>
                        {campaign?.status}
                      </Badge>
                      {campaign?.verified && (
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-400">{formatCurrency(summary.total_raised)}</p>
                  <p className="text-sm text-muted-foreground">of {formatCurrency(summary.goal)} goal</p>
                  <Progress value={progressPercent} className="h-2 mt-2 w-48" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Views</p>
                    <p className="text-2xl font-bold text-foreground">{formatNumber(summary.total_views)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Eye className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Donors</p>
                    <p className="text-2xl font-bold text-foreground">{formatNumber(summary.total_donations)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-teal-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Conversion Rate</p>
                    <p className="text-2xl font-bold text-foreground">{summary.conversion_rate}%</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Percent className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg. Donation</p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(summary.average_donation)}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Views Over Time */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                  Views Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.views_over_time?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.views_over_time.slice(-7).map((day, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-20">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <div className="flex-1 bg-secondary rounded-full h-6 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.min((day.views / Math.max(...analytics.views_over_time.map(d => d.views))) * 100, 100)}%`,
                              minWidth: '40px'
                            }}
                          >
                            <span className="text-xs font-medium text-white">{day.views}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No view data available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Donations Over Time */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  Donations Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.donations_over_time?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.donations_over_time.slice(-7).map((day, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-20">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                        <div className="flex-1 bg-secondary rounded-full h-6 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.min((day.amount / Math.max(...analytics.donations_over_time.map(d => d.amount))) * 100, 100)}%`,
                              minWidth: '60px'
                            }}
                          >
                            <span className="text-xs font-medium text-white">{formatCurrency(day.amount)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No donation data available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Traffic Sources */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Referral Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-teal-400" />
                  Traffic Sources
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.referral_sources?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.referral_sources.slice(0, 6).map((source, idx) => {
                      const total = analytics.referral_sources.reduce((sum, s) => sum + s.count, 0);
                      const percent = ((source.count / total) * 100).toFixed(1);
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{getSourceIcon(source.source)}</span>
                            <span className="font-medium text-foreground capitalize">{source.source}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-muted-foreground">{source.count} visits</span>
                            <Badge variant="outline">{percent}%</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Globe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No traffic source data yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Referrers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ExternalLink className="w-5 h-5 text-amber-400" />
                  Top Referrers
                </CardTitle>
              </CardHeader>
              <CardContent>
                {analytics?.top_referrers?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.top_referrers.slice(0, 6).map((ref, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <MousePointer className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-foreground truncate">{ref.url}</span>
                        </div>
                        <Badge variant="secondary" className="ml-2 flex-shrink-0">{ref.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Share2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No referrer data yet</p>
                    <p className="text-sm mt-2">Share your campaign to see where visitors come from</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tips Card */}
          <Card className="mt-8 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-400" />
                Tips to Improve Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-background/50 rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">📢 Share More</h4>
                  <p className="text-sm text-muted-foreground">
                    Post regular updates on social media to increase visibility and engagement.
                  </p>
                </div>
                <div className="p-4 bg-background/50 rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">📝 Add Updates</h4>
                  <p className="text-sm text-muted-foreground">
                    Keep donors informed with progress updates to encourage repeat donations.
                  </p>
                </div>
                <div className="p-4 bg-background/50 rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">🎯 Set Milestones</h4>
                  <p className="text-sm text-muted-foreground">
                    Create milestones to give donors specific goals to help you reach.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CampaignAnalyticsPage;
